// Work order photos for the office and the mechanic's phone:
// folders (Arrival / Diagnostic / After), the arrival walk-around checklist, and — office only — 👁 "customer sees" per photo.
import { useRef, useState } from 'react';
import { api, uploadPhoto, photoSrc, type Row } from '../lib';
import { Button, Icon, Modal, useToast } from '../ui';
import { FOLDERS, folderOf, folderLabel, FolderCards, Lightbox, BeforeAfter } from '../photos';

const shotsFrom = (raw: unknown): string[] => { try { const a = JSON.parse(String(raw || '[]')); return Array.isArray(a) ? a.map(String) : []; } catch { return []; } };
export const DEFAULT_SHOTS = ['Front', 'Driver side', 'Passenger side', 'Rear', 'Dashboard & mileage', 'Tires'];

/** "Oct 22, 2026, 8:14 AM · 412,000 mi · WO-1007 · #60" — printed on arrival pictures. */
export function stampFor(w: Row) {
  const when = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  const unit = w.unit || w.unit_text;
  return [when, Number(w.mileage) ? `${Number(w.mileage).toLocaleString()} mi` : '', w.number, unit ? `#${unit}` : ''].filter(Boolean).join(' · ');
}
/** Which folder a new photo most likely belongs in, from where the job is. */
function defaultFolder(w: Row, photos: Row[], shots: string[]) {
  const arrival = photos.filter(p => p.folder === 'arrival').length;
  if (w.status === 'New' && arrival < Math.max(1, shots.length)) return 'arrival';
  if (['Ready', 'Completed'].includes(w.status)) return 'after';
  return 'diag';
}

export function WoPhotos({ w, photos, jobs, office, myId, shotsRaw, reload, locked }: {
  w: Row; photos: Row[]; jobs: Row[]; office: boolean; myId: string; shotsRaw?: string; reload: () => void; locked?: boolean;
}) {
  const toast = useToast();
  const shots = shotsRaw === undefined ? DEFAULT_SHOTS : shotsFrom(shotsRaw);
  const [adding, setAdding] = useState(false);
  const [folder, setFolder] = useState<string | null>(null);
  const [view, setView] = useState<{ list: Row[]; i: number } | null>(null);
  const [busyShot, setBusyShot] = useState('');
  const shotInput = useRef<HTMLInputElement>(null);
  const pendingShot = useRef('');
  const jobTitle = (id: string) => jobs.find(j => j.id === id)?.title || '';
  const list: Row[] = photos.map(p => ({ ...p, job_title: p.job_id ? jobTitle(p.job_id) : '' }));
  const taken = new Set(list.filter(p => p.folder === 'arrival' && p.shot).map(p => p.shot));
  const missing = shots.filter(s => !taken.has(s));
  const showChecklist = shots.length > 0 && (missing.length > 0 ? !['Ready', 'Completed'].includes(w.status) : true);

  async function takeShot(files: FileList | null) {
    const shot = pendingShot.current;
    if (!files?.length || !shot) return;
    setBusyShot(shot);
    try { await uploadPhoto(files[0], { kind: 'job', work_order_id: w.id, folder: 'arrival', shot, stamp: stampFor(w) }); toast(`${shot} ✓`); reload(); }
    catch (e) { toast((e as Error).message, 'err'); }
    finally { setBusyShot(''); if (shotInput.current) shotInput.current.value = ''; }
  }
  const patch = async (p: Row, body: Row) => { try { await api(`/api/photos/${p.id}`, { method: 'PATCH', body }); reload(); } catch (e) { toast((e as Error).message, 'err'); } };
  const shareAll = async (ids: string[], shared: boolean) => { try { await api('/api/photos/share', { body: { ids, shared } }); toast(shared ? 'The customer will see them' : 'Hidden from the customer'); reload(); } catch (e) { toast((e as Error).message, 'err'); } };
  const remove = async (p: Row) => { try { await api(`/api/photos/${p.id}`, { method: 'DELETE' }); reload(); } catch (e) { toast((e as Error).message, 'err'); } };
  const canDelete = (p: Row) => office || p.uploaded_by === myId;
  const inFolder = folder === null ? [] : list.filter(p => folderOf(p) === folder);
  const sharedN = list.filter(p => p.shared).length;

  return (
    <div className="wo-photos">
      <div className="wp-top">
        {office && <small className="muted">{sharedN ? `👁 ${sharedN} shown to the customer` : 'The customer sees only the photos you mark 👁'}</small>}
        <span className="spacer" />
        {!locked && <Button icon="camera" onClick={() => setAdding(true)}>Add photos</Button>}
      </div>

      {showChecklist && !locked && (
        <div className={'arrival-check' + (missing.length ? '' : ' done')}>
          <b>📥 Arrival photos {shots.length - missing.length}/{shots.length}</b>
          <div className="chip-row">{shots.map(s => taken.has(s)
            ? <span key={s} className="chip on">✓ {s}</span>
            : <button key={s} type="button" className="chip" disabled={!!busyShot} onClick={() => { pendingShot.current = s; shotInput.current?.click(); }}>{busyShot === s ? 'Uploading…' : `📷 ${s}`}</button>)}</div>
          {missing.length > 0 && <small className="muted">Date, time and mileage are printed on each picture — proof of how the truck came in.</small>}
          <input ref={shotInput} type="file" accept="image/*" capture="environment" hidden onChange={e => void takeShot(e.target.files)} />
        </div>
      )}

      {list.length ? <>
        <FolderCards photos={list} src={photoSrc} onOpen={setFolder}
          badge={office ? l => l.length ? <small className="pf-shared">{l.filter(p => p.shared).length ? `👁 ${l.filter(p => p.shared).length} shown` : 'Hidden from customer'}</small> : null : undefined} />
        {office && <BeforeAfter photos={list.filter(p => p.shared)} src={photoSrc} onOpen={(l, i) => setView({ list: l, i })} />}
      </> : <p className="muted">No photos yet.{!locked ? ' Start with the arrival walk-around.' : ''}</p>}

      {folder !== null && !view && (
        <Modal title={`${FOLDERS.find(f => f.id === folder)?.icon || ''} ${folderLabel(folder)} (${inFolder.length})`} onClose={() => setFolder(null)} wide>
          {office && inFolder.length > 0 && <div className="actions-row pf-bar">
            <Button onClick={() => void shareAll(inFolder.map(p => p.id), true)}>👁 Show all to customer</Button>
            <Button kind="ghost" onClick={() => void shareAll(inFolder.map(p => p.id), false)}>Hide all</Button>
          </div>}
          {!inFolder.length && <p className="muted pad">No photos in this folder.</p>}
          <div className="pf-grid">{inFolder.map((p, i) => (
            <div key={p.id} className={'pf-tile' + (p.shared ? ' shared' : '')}>
              <button type="button" className="pf-img" onClick={() => setView({ list: inFolder, i })}><img src={photoSrc(p.id)} alt={p.note || ''} loading="lazy" /></button>
              {(p.shot || p.job_title) && <span className="pf-tag">{p.shot || p.job_title}</span>}
              <div className="pf-acts">
                {office && <button type="button" className={'pf-eye' + (p.shared ? ' on' : '')} title={p.shared ? 'Customer sees it — tap to hide' : 'Hidden — tap to show the customer'} onClick={() => void patch(p, { shared: !p.shared })}>{p.shared ? '👁 Shown' : '🚫 Hidden'}</button>}
                {canDelete(p) && <button type="button" className="pf-x" title="Delete (goes to the trash)" aria-label="Delete photo" onClick={() => void remove(p)}><Icon name="x" size={14} /></button>}
              </div>
            </div>))}</div>
        </Modal>
      )}
      {view && <Lightbox photos={view.list} start={view.i} src={photoSrc} onClose={() => setView(null)} extra={office ? p => (
        <div className="pv-tools">
          <button type="button" className={'pf-eye' + (p.shared ? ' on' : '')} onClick={() => void patch(p, { shared: !p.shared }).then(() => setView(v => v && { ...v, list: v.list.map(x => x.id === p.id ? { ...x, shared: x.shared ? 0 : 1 } : x) }))}>{p.shared ? '👁 Customer sees it' : '🚫 Hidden from customer'}</button>
          <select value={folderOf(p)} aria-label="Folder" onChange={e => { const f = e.target.value; void patch(p, { folder: f }); setView(v => v && { ...v, list: v.list.map(x => x.id === p.id ? { ...x, folder: f } : x) }); }}>
            {FOLDERS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}</select>
          {jobs.length > 0 && <select value={p.job_id || ''} aria-label="Job" onChange={e => { const j = e.target.value; void patch(p, { job_id: j || null }); setView(v => v && { ...v, list: v.list.map(x => x.id === p.id ? { ...x, job_id: j, job_title: jobTitle(j) } : x) }); }}>
            <option value="">No job</option>{jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}</select>}
        </div>) : undefined} />}
      {adding && <AddPhotos w={w} jobs={jobs} start={defaultFolder(w, list, shots)} onClose={() => setAdding(false)} onDone={() => { setAdding(false); reload(); }} />}
    </div>
  );
}

/** Pick the folder (and job), then take or choose the pictures. */
export function AddPhotos({ w, jobs, start, job: forJob, onClose, onDone }: { w: Row; jobs: Row[]; start: string; job?: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [folder, setFolder] = useState(start);
  const [job, setJob] = useState(forJob || '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  const gal = useRef<HTMLInputElement>(null);
  async function go(files: FileList | null) {
    if (!files?.length) return;
    const all = Array.from(files);
    try {
      for (let i = 0; i < all.length; i++) {
        setBusy(`Uploading ${i + 1} of ${all.length}…`);
        await uploadPhoto(all[i], { kind: 'job', work_order_id: w.id, folder, job_id: job || undefined, note, stamp: folder === 'arrival' ? stampFor(w) : undefined });
      }
      toast(all.length > 1 ? `${all.length} photos added to ${folderLabel(folder)}` : `Photo added to ${folderLabel(folder)}`);
      onDone();
    } catch (e) { toast((e as Error).message, 'err'); setBusy(''); }
  }
  return (
    <Modal title="Add photos" onClose={onClose}>
      <div className="form-body">
        <div className="seg-choice full" role="radiogroup" aria-label="Folder">
          {FOLDERS.filter(f => f.id).map(f => <button key={f.id} type="button" role="radio" aria-checked={folder === f.id} className={folder === f.id ? 'on' : ''} onClick={() => setFolder(f.id)}>{f.icon} {f.label}</button>)}
        </div>
        <p className="form-note full">{FOLDERS.find(f => f.id === folder)?.hint}{folder === 'arrival' ? ' — date, time and mileage are printed on the picture.' : ''}</p>
        {jobs.length > 0 && folder !== 'arrival' && <label className="field full"><span>Job (optional — for Before / After)</span>
          <select value={job} onChange={e => setJob(e.target.value)}><option value="">No job</option>{jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}</select></label>}
        <label className="field full"><span>Note (optional)</span><input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. left steer tire cut, brake chamber leaking" maxLength={200} /></label>
        <input ref={ref} type="file" accept="image/*" capture="environment" multiple hidden onChange={e => void go(e.target.files)} />
        <input ref={gal} type="file" accept="image/*" multiple hidden onChange={e => void go(e.target.files)} />
        {busy ? <Button kind="primary" className="full big" disabled>{busy}</Button> : <div className="photo-two full">
          <Button kind="primary" icon="camera" className="big" onClick={() => ref.current?.click()}>Take photo</Button>
          <Button className="big" onClick={() => gal.current?.click()}>🖼 Choose from gallery</Button>
        </div>}
      </div>
    </Modal>
  );
}
